"use client";

import { useContext, useState, useEffect } from "react";
import {
  Flex,
  VStack,
  Input,
  Button,
  Text,
  useMediaQuery,
  Image,
  Box,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import HeaderTools from "../../components/HeaderTools";
import api from "../../services/api";
import { AuthContext } from "../../contexts/AuthContext";
import { PRIMARY_COLOR } from "../../constants/colors";

export default function AccessSettings() {
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [photo, setPhoto] = useState(null);

  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [previewPhoto, setPreviewPhoto] = useState(null);

  useEffect(() => {
    if (user?.info?.foto) {
      setPreviewPhoto(user.info.foto);
    }
  }, [user]);

  const getCompanyPhoto = async () => {
    await api
      .get("/admin/profile-photo")
      .then((response) => {
        setPhoto(response.data.photo);
      })
      .catch((error) => {
        console.log("erro ao buscar foto", error);
      });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione uma imagem válida");
      return;
    }

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewPhoto(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload para o backend
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        "/admin/modify-profile-picture",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        toast.success("Foto da empresa atualizada com sucesso!");
        await getCompanyPhoto();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Erro ao enviar foto. Tente novamente.",
      );
      // Reverter preview se falhar
      setPreviewPhoto(user?.info?.foto || null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const validatePasswordsMatch = () => {
    const newErrors = {};

    if (!currentPassword.trim()) {
      newErrors.currentPassword = "Senha atual é obrigatória";
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = "Nova senha é obrigatória";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirmar senha é obrigatória";
    }

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      newErrors.confirmPassword = "As senhas não correspondem";
    }

    if (newPassword && newPassword.length < 6) {
      newErrors.newPassword = "A senha deve ter no mínimo 6 caracteres";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validatePasswordsMatch();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setLoading(true);

      await api.patch("/admin/change-password", {
        currentPassword,
        newPassword,
      });

      toast.success("Senha alterada com sucesso!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        navigate("/painel");
      }, 2000);
    } catch (error) {
      if (error.response?.status === 401) {
        setErrors({
          currentPassword: "Senha atual incorreta",
        });
        toast.error("Senha atual incorreta");
      } else {
        toast.error(
          error.response?.data?.message ||
            "Erro ao alterar a senha. Tente novamente.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/painel");
  };

  useEffect(() => {
    getCompanyPhoto();
  }, []);

  return (
    <Flex flexDirection="column" width="100%" minH="100vh" bg="#f8f9fa">
      <HeaderTools title="Configurações de Acesso" onBack={handleBack} />

      <Flex
        flexDirection={isLargerThan768 ? "row" : "column"}
        width="100%"
        alignItems={isLargerThan768 ? "flex-start" : "center"}
        padding="20px"
        marginTop="20px"
        gap="20px"
        maxW={isLargerThan768 ? "1200px" : "100%"}
      >
        {/* Card Alterar Senha */}
        <Flex
          flex={isLargerThan768 ? 1 : "auto"}
          width={isLargerThan768 ? "auto" : "100%"}
          flexDirection="column"
          bg="white"
          padding="30px"
          borderRadius="8px"
          boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
          minH="100%"
        >
          <Text
            fontSize="18px"
            fontWeight="600"
            marginBottom="20px"
            textAlign="center"
          >
            Alterar Senha
          </Text>

          <form onSubmit={handleSubmit}>
            <VStack spacing="5">
              {/* Senha Atual */}
              <Box width="100%">
                <Text fontWeight="600" marginBottom="8px">
                  Senha Atual
                </Text>
                <Input
                  type="password"
                  placeholder="Digite sua senha atual"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    if (errors.currentPassword) {
                      const newErrors = { ...errors };
                      delete newErrors.currentPassword;
                      setErrors(newErrors);
                    }
                  }}
                  padding="12px"
                  border={
                    errors.currentPassword
                      ? "1px solid #e74c3c"
                      : "1px solid #e0e0e0"
                  }
                  borderRadius="4px"
                  _focus={{
                    borderColor: PRIMARY_COLOR,
                    boxShadow: `0 0 0 1px ${PRIMARY_COLOR}`,
                  }}
                />
                {errors.currentPassword && (
                  <Text fontSize="12px" color="#e74c3c" marginTop="5px">
                    {errors.currentPassword}
                  </Text>
                )}
              </Box>

              {/* Nova Senha */}
              <Box width="100%">
                <Text fontWeight="600" marginBottom="8px">
                  Nova Senha
                </Text>
                <Input
                  type="password"
                  placeholder="Digite sua nova senha"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (errors.newPassword) {
                      const newErrors = { ...errors };
                      delete newErrors.newPassword;
                      setErrors(newErrors);
                    }
                  }}
                  padding="12px"
                  border={
                    errors.newPassword
                      ? "1px solid #e74c3c"
                      : "1px solid #e0e0e0"
                  }
                  borderRadius="4px"
                  _focus={{
                    borderColor: PRIMARY_COLOR,
                    boxShadow: `0 0 0 1px ${PRIMARY_COLOR}`,
                  }}
                />
                {errors.newPassword && (
                  <Text fontSize="12px" color="#e74c3c" marginTop="5px">
                    {errors.newPassword}
                  </Text>
                )}
                <Text fontSize="12px" color="#666" marginTop="5px">
                  Mínimo 6 caracteres
                </Text>
              </Box>

              {/* Confirmar Senha */}
              <Box width="100%">
                <Text fontWeight="600" marginBottom="8px">
                  Confirmar Senha
                </Text>
                <Input
                  type="password"
                  placeholder="Confirme sua nova senha"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) {
                      const newErrors = { ...errors };
                      delete newErrors.confirmPassword;
                      setErrors(newErrors);
                    }
                  }}
                  padding="12px"
                  border={
                    errors.confirmPassword
                      ? "1px solid #e74c3c"
                      : "1px solid #e0e0e0"
                  }
                  borderRadius="4px"
                  _focus={{
                    borderColor: PRIMARY_COLOR,
                    boxShadow: `0 0 0 1px ${PRIMARY_COLOR}`,
                  }}
                />
                {errors.confirmPassword && (
                  <Text fontSize="12px" color="#e74c3c" marginTop="5px">
                    {errors.confirmPassword}
                  </Text>
                )}
              </Box>

              {/* Buttons */}
              <Flex gap="10px" width="100%" marginTop="20px">
                <Button
                  flex={1}
                  variant="outline"
                  onClick={handleBack}
                  isDisabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  flex={1}
                  bg={PRIMARY_COLOR}
                  color="white"
                  fontWeight="600"
                  type="submit"
                  isLoading={loading}
                  _hover={{
                    bg: "#8c2db8",
                  }}
                >
                  Alterar Senha
                </Button>
              </Flex>
            </VStack>
          </form>

          <Text
            fontSize="12px"
            color="#999"
            marginTop="20px"
            textAlign="center"
          >
            Por favor, use uma senha segura para proteger sua conta.
          </Text>
        </Flex>

        {/* Card Foto de Perfil */}
        <Flex
          flex={isLargerThan768 ? 1 : "auto"}
          width={isLargerThan768 ? "auto" : "100%"}
          flexDirection="column"
          bg="white"
          padding="30px"
          borderRadius="8px"
          boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
          alignItems="center"
          minW={isLargerThan768 ? "300px" : "auto"}
          minH="100%"
        >
          <Text
            fontSize="18px"
            fontWeight="600"
            marginBottom="20px"
            textAlign="center"
          >
            Foto da Empresa
          </Text>
          {photo && (
            <Flex width="100%" mb="20px" justifyContent="center">
              <img src={`https://api.aqvex.com.br${photo}`} alt="Perfil" />
            </Flex>
          )}

          {/* Preview Foto */}
          <Flex
            width="200px"
            height="200px"
            borderRadius="12px"
            bg="#f0f0f0"
            alignItems="center"
            justifyContent="center"
            marginBottom="20px"
            border="2px dashed #e0e0e0"
            overflow="hidden"
          >
            {previewPhoto ? (
              <Image
                src={previewPhoto}
                alt="Foto de perfil"
                width="100%"
                height="100%"
                objectFit="cover"
              />
            ) : (
              <Text fontSize="12px" color="#999" textAlign="center">
                Nenhuma foto selecionada
              </Text>
            )}
          </Flex>

          {/* Input File */}
          <Box
            width="100%"
            padding="15px"
            border="2px dashed #e0e0e0"
            borderRadius="8px"
            textAlign="center"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{
              borderColor: PRIMARY_COLOR,
              bg: "#f9f9f9",
            }}
            position="relative"
            overflow="hidden"
            marginBottom="15px"
          >
            <Input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              display="none"
              id="photo-input"
              isDisabled={uploadingPhoto}
            />
            <label htmlFor="photo-input" style={{ cursor: "pointer" }}>
              <Text fontSize="14px" fontWeight="500" color={PRIMARY_COLOR}>
                {uploadingPhoto ? "Enviando..." : "Selecionar Foto"}
              </Text>
              <Text fontSize="12px" color="#999" marginTop="5px">
                ou arraste uma imagem
              </Text>
            </label>
          </Box>

          <Text fontSize="12px" color="#999" textAlign="center">
            Tamanho máximo: 5MB
            <br />
            Formatos: JPG, PNG, JPEG
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
